// File: client_serena/src/pages/institutional/HomePage.js

import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";

const engines = [
  {
    number: "01",
    title: "Science",
    heading: "Generate and translate evidence.",
    text:
      "We develop and translate evidence for preventive and precision healthcare, with African populations, data and health realities at the centre.",
    link: "Explore the Institute",
    href: "/institute",
  },
  {
    number: "02",
    title: "Technology",
    heading: "Turn health intelligence into usable tools.",
    text:
      "We build technologies that help people, families and health systems make better use of health information.",
    link: "Explore the technology",
    href: "https://selense.serenaspace.com",
  },
  {
    number: "03",
    title: "Clinical & Health-System Activation",
    heading: "Make promising innovations work in real pathways.",
    text:
      "We design, activate, measure and improve the pathways through which health innovations become usable care.",
    link: "Explore our work",
    href: "/work#activation",
  },
];

const pathway = [
  "Evidence",
  "Translation",
  "Technology",
  "Pathway",
  "Outcome",
  "New evidence",
];

function HomePage() {
  return (
    <Box className="serena-home">

      {/* =========================================================
          HERO
          ========================================================= */}
      <Box
        position="relative"
        overflow="hidden"
        bg="#F3F0E8"
      >
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 16, md: 24, lg: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 16 }}
            alignItems="center"
          >
            <Stack spacing={{ base: 7, md: 9 }} maxW="720px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.16em"
                textTransform="uppercase"
                color="#087F73"
              >
                From evidence to action
              </Text>

              <Heading
                as="h1"
                fontFamily="'Poppins', sans-serif"
                fontSize={{
                  base: "3xl",
                  sm: "4xl",
                  md: "5xl",
                  lg: "6xl",
                }}
                lineHeight="1.04"
                letterSpacing="-0.045em"
                fontWeight="600"
                color="#17211F"
              >
                Building what it takes to move health science into practice.
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg", lg: "xl" }}
                lineHeight="1.75"
                color="#4B5753"
                maxW="650px"
              >
                Serena Space is building the scientific, technological and
                translational infrastructure needed to advance preventive and
                precision healthcare in Africa.
              </Text>

              <Flex
                direction={{ base: "column", sm: "row" }}
                gap={4}
                pt={2}
              >
                <Button
                  as="a"
                  href="/work"
                  size="lg"
                  px={7}
                  bg="#087F73"
                  color="white"
                  borderRadius="full"
                  fontWeight="600"
                  _hover={{
                    bg: "#06695F",
                  }}
                >
                  Explore the Work
                </Button>

                <Button
                  as="a"
                  href="/partner"
                  size="lg"
                  px={7}
                  variant="outline"
                  borderColor="#AAB5B0"
                  color="#17211F"
                  borderRadius="full"
                  fontWeight="600"
                  _hover={{
                    bg: "whiteAlpha.700",
                    borderColor: "#087F73",
                  }}
                >
                  Partner with Serena
                </Button>
              </Flex>
            </Stack>

            <Box
              position="relative"
              borderRadius="2xl"
              overflow="hidden"
              minH={{ base: "320px", md: "460px", lg: "560px" }}
            >
              <Image
                src="/images/institutional/serena8.png"
                alt="Serena Space — advancing preventive and precision healthcare in Africa"
                w="100%"
                h="100%"
                minH={{ base: "320px", md: "460px", lg: "560px" }}
                objectFit="cover"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>


      {/* =========================================================
          THE TRANSLATION GAP
          ========================================================= */}
      <Box bg="#FFFFFF">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 12, md: 20 }}
            alignItems="center"
          >
            <Stack spacing={6}>
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#087F73"
              >
                The translation gap
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                lineHeight="1.08"
                letterSpacing="-0.04em"
                fontWeight="600"
                color="#17211F"
              >
                Health science can move faster than healthcare can absorb it.
              </Heading>
            </Stack>

            <Stack spacing={5}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#4B5753"
              >
                A promising diagnostic, therapy, dataset or technology does
                not become better care simply because it exists.
              </Text>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#4B5753"
              >
                The pathway between scientific possibility and patient
                outcome has to be built, tested and measured.
              </Text>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                fontWeight="600"
                color="#17211F"
              >
                That is the space Serena is building in.
              </Text>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>


      {/* =========================================================
          PATHWAY
          ========================================================= */}
      <Box
        bg="#17211F"
        color="white"
        overflow="hidden"
      >
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 16, md: 20, lg: 24 }}
        >
          <Stack spacing={12}>
            <Box maxW="700px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#A9D8D1"
                mb={5}
              >
                The pathway
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl" }}
                lineHeight="1.1"
                letterSpacing="-0.035em"
                fontWeight="600"
              >
                From evidence to outcomes — and back again.
              </Heading>
            </Box>

            <Flex
              direction={{ base: "column", md: "row" }}
              align={{ base: "stretch", md: "center" }}
              gap={{ base: 4, md: 0 }}
              overflowX={{ md: "auto" }}
              pb={2}
            >
              {pathway.map((item, index) => (
                <React.Fragment key={item}>
                  <Box
                    flex="1"
                    minW={{ md: "150px" }}
                    px={{ base: 5, md: 4 }}
                    py={5}
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius={{ base: "lg", md: "none" }}
                    textAlign={{ base: "left", md: "center" }}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      letterSpacing="0.02em"
                    >
                      {item}
                    </Text>
                  </Box>

                  {index < pathway.length - 1 && (
                    <Text
                      display={{ base: "none", md: "block" }}
                      px={3}
                      color="#A9D8D1"
                      fontSize="xl"
                    >
                      →
                    </Text>
                  )}
                </React.Fragment>
              ))}
            </Flex>
          </Stack>
        </Container>
      </Box>


      {/* =========================================================
          THREE ENGINES
          ========================================================= */}
      <Box bg="#F9FAF8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={{ base: 12, md: 16 }}>
            <Box maxW="760px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#087F73"
                mb={5}
              >
                How Serena works
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                lineHeight="1.08"
                letterSpacing="-0.04em"
                fontWeight="600"
                color="#17211F"
              >
                Three engines. One translational mission.
              </Heading>
            </Box>

            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={{ base: 6, lg: 8 }}
            >
              {engines.map((engine) => (
                <Box
                  key={engine.number}
                  bg="white"
                  p={{ base: 7, md: 8, lg: 10 }}
                  border="1px solid"
                  borderColor="#E4E8E5"
                  borderRadius="xl"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="#087F73"
                    mb={8}
                  >
                    {engine.number}
                  </Text>

                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    color="#68736F"
                    mb={4}
                  >
                    {engine.title}
                  </Text>

                  <Heading
                    as="h3"
                    fontFamily="'Poppins', sans-serif"
                    fontSize={{ base: "xl", md: "2xl" }}
                    lineHeight="1.2"
                    letterSpacing="-0.025em"
                    fontWeight="600"
                    color="#17211F"
                    mb={5}
                  >
                    {engine.heading}
                  </Heading>

                  <Text
                    fontSize="md"
                    lineHeight="1.75"
                    color="#56625E"
                    mb={8}
                  >
                    {engine.text}
                  </Text>

                  <Button
                    as="a"
                    href={engine.href}
                    variant="link"
                    color="#087F73"
                    fontWeight="600"
                    rightIcon={<span aria-hidden="true">→</span>}
                  >
                    {engine.link}
                  </Button>
                </Box>
              ))}
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>


      {/* =========================================================
          WORK IN MOTION
          ========================================================= */}
      <Box bg="#FFFFFF">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 16 }}
            alignItems="center"
          >
            <Box
              borderRadius="2xl"
              overflow="hidden"
              minH={{ base: "320px", md: "460px" }}
            >
              <Image
                src="/images/institutional/serena2.png"
                alt="Serena Space research and implementation work"
                w="100%"
                h={{ base: "320px", md: "460px" }}
                objectFit="cover"
              />
            </Box>

            <Stack spacing={6} maxW="650px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#087F73"
              >
                Work in motion
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                lineHeight="1.08"
                letterSpacing="-0.04em"
                fontWeight="600"
                color="#17211F"
              >
                Building evidence, technology and pathways in the real world.
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#56625E"
              >
                Serena works across research, technology and healthcare
                environments to turn promising capabilities into things that
                can actually be used, measured and improved.
              </Text>

              <Button
                as="a"
                href="/work"
                alignSelf="flex-start"
                variant="outline"
                borderColor="#AAB5B0"
                borderRadius="full"
                px={7}
                color="#17211F"
                _hover={{
                  borderColor: "#087F73",
                  bg: "#F3F8F7",
                }}
              >
                See selected work
              </Button>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>


      {/* =========================================================
          SELENSE
          ========================================================= */}
      <Box bg="#F3F0E8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 16 }}
            alignItems="center"
          >
            <Stack spacing={6} maxW="650px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#087F73"
              >
                Health technology
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                lineHeight="1.08"
                letterSpacing="-0.04em"
                fontWeight="600"
                color="#17211F"
              >
                Start with one person you love.
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#56625E"
              >
                Selense helps you bring your loved one's health story
                together, keep it in one place and understand what matters
                next.
              </Text>

              <Button
                as="a"
                href="https://selense.serenaspace.com"
                target="_blank"
                rel="noopener noreferrer"
                alignSelf="flex-start"
                bg="#087F73"
                color="white"
                borderRadius="full"
                px={7}
                _hover={{
                  bg: "#06695F",
                }}
              >
                Explore Selense
              </Button>
            </Stack>

            <Box
              borderRadius="2xl"
              overflow="hidden"
              minH={{ base: "320px", md: "440px" }}
            >
              <Image
                src="/images/institutional/serena4.png"
                alt="Selense health technology"
                w="100%"
                h={{ base: "320px", md: "440px" }}
                objectFit="cover"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>


      {/* =========================================================
          INSTITUTE + COMMUNITY
          ========================================================= */}
      <Box bg="#FFFFFF">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 6, lg: 8 }}
          >
            <Box
              bg="#17211F"
              color="white"
              p={{ base: 8, md: 10, lg: 12 }}
              borderRadius="xl"
              minH={{ md: "430px" }}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.15em"
                  textTransform="uppercase"
                  color="#A9D8D1"
                  mb={5}
                >
                  Serena Space Institute
                </Text>

                <Heading
                  fontFamily="'Poppins', sans-serif"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  lineHeight="1.1"
                  letterSpacing="-0.035em"
                  fontWeight="600"
                  mb={6}
                >
                  Research that moves.
                </Heading>

                <Text
                  fontSize="md"
                  lineHeight="1.8"
                  color="whiteAlpha.700"
                  maxW="560px"
                >
                  A scientific and translational platform for preventive and
                  precision health, African health data, research capacity and
                  scientific exchange.
                </Text>
              </Box>

              <Button
                as="a"
                href="/institute"
                alignSelf="flex-start"
                variant="outline"
                borderColor="whiteAlpha.400"
                color="white"
                borderRadius="full"
                px={6}
                _hover={{
                  bg: "whiteAlpha.100",
                }}
              >
                Visit the Institute
              </Button>
            </Box>

            <Box
              overflow="hidden"
              borderRadius="xl"
              minH={{ md: "430px" }}
            >
              <Image
                src="/images/institutional/serena6.png"
                alt="Serena Space Institute"
                w="100%"
                h="100%"
                minH={{ base: "320px", md: "430px" }}
                objectFit="cover"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>


      {/* =========================================================
          CLINICAL & HEALTH-SYSTEM ACTIVATION
          ========================================================= */}
      <Box bg="#F9FAF8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={8} maxW="900px">
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.15em"
              textTransform="uppercase"
              color="#087F73"
            >
              Clinical & health-system activation
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              lineHeight="1.08"
              letterSpacing="-0.04em"
              fontWeight="600"
              color="#17211F"
            >
              Turning promising health innovations into working pathways.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="#56625E"
              maxW="760px"
            >
              We work with existing clinical and health-system capabilities
              to map, design, activate, standardise and measure the pathways
              around promising diagnostics, technologies and other health
              innovations.
            </Text>

            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={4}
              pt={2}
            >
              {["Map", "Design", "Activate", "Standardise", "Measure", "Translate"].map(
                (step) => (
                  <Box
                    key={step}
                    px={5}
                    py={3}
                    border="1px solid"
                    borderColor="#D7DEDA"
                    borderRadius="full"
                    bg="white"
                  >
                    <Text fontSize="sm" fontWeight="600" color="#34413D">
                      {step}
                    </Text>
                  </Box>
                )
              )}
            </Flex>
          </Stack>
        </Container>
      </Box>


      {/* =========================================================
          FINAL CTA
          ========================================================= */}
      <Box bg="#087F73" color="white">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={7} maxW="800px">
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.15em"
              textTransform="uppercase"
              color="#C9E9E4"
            >
              Build with us
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              lineHeight="1.08"
              letterSpacing="-0.04em"
              fontWeight="600"
            >
              The future of preventive and precision healthcare will need
              more than promising science.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="whiteAlpha.800"
              maxW="700px"
            >
              It will need evidence, technology, people and pathways that
              work together.
            </Text>

            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={4}
              pt={2}
            >
              <Button
                as="a"
                href="/partner"
                size="lg"
                px={7}
                bg="white"
                color="#087F73"
                borderRadius="full"
                fontWeight="600"
                _hover={{
                  bg: "#F3F7F6",
                }}
              >
                Partner with Serena
              </Button>

              <Button
                as="a"
                href="/institute"
                size="lg"
                px={7}
                variant="outline"
                borderColor="whiteAlpha.500"
                color="white"
                borderRadius="full"
                fontWeight="600"
                _hover={{
                  bg: "whiteAlpha.100",
                }}
              >
                Explore the Institute
              </Button>
            </Flex>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}

export default HomePage;